import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import { diffCampaignVsRow } from './diffCampaignVsRow';

// Pre-P2b-3 polish round 3 (Fix 1 + Fix 3): unified "row has been
// modified from its baseline" signal for the master grid's pencil-icon
// dot. Combines two independent drift sources:
//
//   1. Campaign content drift — the row's snapshotted content fields
//      diverge from the current Campaign (diffCampaignVsRow).
//   2. Date drift — the AM has edited startDate / endDate away from
//      their creation-time *Original snapshots.
//
// Edge cases — the dot must NOT show on:
//   - Opt-out rows: content state is intentionally cleared, drift is
//     meaningless. Dates may differ from rollout defaults but the row
//     is parked, so the signal would be noise.
//   - Unassigned rows: no Campaign reference state to drift from on the
//     content side. (Date drift still possible but the row is in a
//     transitional state; no dot until a Campaign is assigned.)
//
// Helpers are pure and tested independently.

// Per-cell date drift helpers (Pre-P2b-3 polish round 4). Used by both
// the master grid (per-cell orange dot next to startDate / endDate)
// and the row editor (per-input amber ModifiedBadge). Pure, gated on:
//
//   1. rowType === 'opt_out' → false. Opted-out rows clear startDate /
//      endDate to null while leaving *Original intact, so a naïve
//      compare flags drift. The row is parked though, so the signal
//      is noise. Skip it everywhere these helpers are called.
//   2. *Original === undefined (legacy row pre-dating the field) →
//      false. Graceful degradation; the inline-patch backfill seeds
//      *Original on the AM's first edit, so drift detection catches
//      up from that touch forward.
//   3. otherwise → strict !== compare (handles null-vs-null = no
//      drift, null-vs-non-null = drift).
export function hasStartDateDrift(row: AccountCampaign): boolean {
  if (row.rowType === 'opt_out') return false;
  if (row.startDateOriginal === undefined) return false;
  return row.startDate !== row.startDateOriginal;
}

export function hasEndDateDrift(row: AccountCampaign): boolean {
  if (row.rowType === 'opt_out') return false;
  if (row.endDateOriginal === undefined) return false;
  return row.endDate !== row.endDateOriginal;
}

// True iff the row's startDate or endDate diverges from its
// creation-time snapshot. Used by hasRowModifications (the unified
// pencil-icon dot). Layered over the per-cell helpers so the opt_out
// skip + legacy-undefined rule live in one place.
export function hasDateDrift(row: AccountCampaign): boolean {
  return hasStartDateDrift(row) || hasEndDateDrift(row);
}

// True iff the row has any drift worth surfacing on the master grid.
// `campaign` may be null when the row has no campaignId (unassigned)
// or when the lookup failed; the helper handles both as "no content
// drift to compare".
export function hasRowModifications(
  row: AccountCampaign,
  campaign: Campaign | null | undefined,
): boolean {
  // Skip the parked / transitional row types — see header.
  if (row.rowType === 'opt_out' || row.rowType === 'unassigned') return false;
  if (row.campaignId === null) return false;

  // Content drift: only meaningful when we have the Campaign in hand.
  if (campaign && diffCampaignVsRow(row, campaign).size > 0) return true;

  // Date drift: independent of Campaign state — a campaign-type row
  // with edited dates surfaces the dot even when content is clean.
  if (hasDateDrift(row)) return true;

  return false;
}
